<!-- テナント設定 > 最終処分場フォーム -->
<template>
  <div>
    <validation-observer ref="FinalDisposalSiteFormValidator">
      <v-form>
        <v-card flat>
          <v-toolbar flat>
            <v-toolbar-title>処分事業場{{ $route.params.id ? '編集' : '追加' }}（再資源）</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: true, alpha_num_only: true, min: 4, max: 10 }"
                  name="事業場コード"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="final_disposal_site.code"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="事業場コード"
                    outlined
                    persistent-placeholder
                    placeholder="例）K001"
                  >
                    <template v-slot:label>
                      <span>事業場コード</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.code?.length || 0 }} / 10)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col
                cols="12"
                md="6"
              >
                最近使用したコード:&nbsp;<strong>{{ final_disposal_site.recent_registered_codes }}</strong>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  name="処分業者名"
                  rules="max:60"
                >
                  <v-text-field
                    v-model="final_disposal_site.trader_name"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="処分業者名"
                    outlined
                    persistent-placeholder
                    placeholder="例）株式会社〇〇〇〇"
                  >
                    <template v-slot:label>
                      <span>処分業者名</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.name?.length || 0 }} / 60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>

              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ required: false, alpha_num_only: true, length: 6 }"
                  name="許可番号（下6桁）"
                >
                  <v-text-field
                    v-model="final_disposal_site.recycle_permission_number"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    hint="下6桁を入力してください"
                    label="許可番号（再資源化）"
                    outlined
                    persistent-placeholder
                    placeholder=""
                  >
                    <template v-slot:label>
                      <span>許可番号（下6桁）</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.recycle_permission_number?.length || 0 }} / 6)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>

              <v-col cols="12" md="4">
                <v-radio-group
                  v-model="final_disposal_site.location_type"
                  label="事業場区分"
                  row
                  dense
                  hide-details="auto"
                  class="pa-0 ma-0"
                >
                  <v-radio
                    label="中間"
                    value="only_middle"
                  />
                  <v-radio
                    label="最終(再生含む)"
                    value="only_last"
                  />
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="処分事業場"
                  rules="required|max:60"
                >
                  <v-text-field
                    v-model="final_disposal_site.name"
                    :error-messages="errors"
                    dense
                    hide-details="auto"
                    label="処分事業場"
                    outlined
                    persistent-placeholder
                    placeholder="例）△△△△埋立地"
                  >
                    <template v-slot:label>
                      <span>処分事業場名</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.name?.length || 0 }} / 60)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>

            <!-- 郵便番号 -->
            <v-row>
              <v-col cols="12" md="5">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{zip_code: true, required: true, max:8 }"
                  name="郵便番号"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="final_disposal_site.zip_code"
                    :error-messages="errors"
                    dense
                    label="郵便番号"
                    hide-details="auto"
                    hint="ハイフンを含めて入力してください"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="例）111-1111"
                    required
                  >
                    <template v-slot:label>
                      <span>郵便番号</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.zip_code?.length || 0 }} / 8)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                    <template v-slot:append-outer>
                      <zip-code-search
                        :zip-code="final_disposal_site.zip_code"
                        @zipAddress="setZipAddress"
                      />
                      &nbsp;
                      <v-btn
                        outlined
                        small
                        @click="searchZipButtonClickEvent"
                      >
                        住所→郵便番号
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /郵便番号 -->

            <!-- 都道府県／市区町村 -->
            <v-row>
              <v-col cols="12" md="3">
                <validation-provider
                  v-slot="{ errors }"
                  name="都道府県"
                  rules="required"
                  class="requiredMark"
                >
                  <v-autocomplete
                    v-model="final_disposal_site.prefecture_code"
                    :error-messages="errors"
                    :items="prefList"
                    dense
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    label="都道府県"
                    outlined
                    persistent-placeholder
                    placeholder="例）東京都"
                  />
                </validation-provider>
              </v-col>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="市区町村"
                  rules="required|full_width|max:20"
                  class="requiredMark"
                >
                  <v-text-field
                    v-model="final_disposal_site.address_1"
                    :error-messages="errors"
                    dense
                    label="市区町村"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）中央区"
                  >
                    <template v-slot:label>
                      <span>市区町村</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.address_1?.length || 0 }} / 20)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /都道府県／市区町村 -->

            <!-- 町域 -->
            <v-row>
              <v-col cols="12" md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="町域"
                  rules="full_width|max:40"
                >
                  <v-text-field
                    v-model="final_disposal_site.address_2"
                    :error-messages="errors"
                    dense
                    label="町域"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）日本橋兜町"
                  >
                    <template v-slot:label>
                      <span>町域</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.address_2?.length || 0 }} / 40)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /町域 -->

            <!-- 詳細住所 -->
            <v-row>
              <v-col md="9">
                <validation-provider
                  v-slot="{ errors }"
                  name="詳細住所"
                  rules="max:50"
                >
                  <v-text-field
                    v-model="final_disposal_site.address_3"
                    :error-messages="errors"
                    dense
                    label="詳細住所"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）１－２－３"
                  >
                    <template v-slot:label>
                      <span>詳細住所</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.address_3?.length || 0 }} / 50)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /詳細住所 -->

            <!-- 電話番号 -->
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  v-slot="{ errors }"
                  name="電話番号"
                  :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
                >
                  <v-text-field
                    v-model="final_disposal_site.phone_number"
                    :error-messages="errors"
                    dense
                    hint="電話番号を「-」ハイフン「( )」込みで入力します"
                    persistent-hint
                    label="電話番号"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）000-0000-0000"
                  >
                    <template v-slot:label>
                      <span>電話番号</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.phone_number?.length || 0 }} / 15)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /電話番号 -->

            <!-- 町域 -->
            <v-card-title>
              <span style="color: #0074BA">電子契約サービス用項目</span>
            </v-card-title>
            <v-row>
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  name="許可番号"
                  rules="max:40"
                >
                  <v-text-field
                    v-model="final_disposal_site.permission_number"
                    :error-messages="errors"
                    dense
                    label="許可番号"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）第1234567890号"
                  >
                    <template v-slot:label>
                      <span>許可番号</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.permission_number?.length || 0 }} / 40)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  name="処分方法"
                  rules="max:40"
                >
                  <v-text-field
                    v-model="final_disposal_site.disposal_method_name"
                    :error-messages="errors"
                    dense
                    label="処分方法"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）管理型埋立"
                  >
                    <template v-slot:label>
                      <span>処分方法</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.disposal_method_name?.length || 0 }} / 40)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  name="処理能力"
                  rules="max:40"
                >
                  <v-text-field
                    v-model="final_disposal_site.processing_power"
                    :error-messages="errors"
                    dense
                    label="処理能力"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    placeholder="例）30000立米 8t/日"
                  >
                    <template v-slot:label>
                      <span>処理能力</span>
                      <span class="ml-1">
                        ({{ final_disposal_site.processing_power?.length || 0 }} / 40)
                      </span>
                    </template>
                    <template v-slot:counter></template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
            <!-- /町域 -->
            <v-row>
              <v-col cols="12" md="4">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
                  name="表示順"
                >
                  <v-text-field
                    v-model="final_disposal_site.sort_priority"
                    :error-messages="errors"
                    dense
                    :show-size="1000"
                    label="表示順"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                    reverse
                    type="number"
                    @keydown.69.prevent
                  />
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn
              @click="cancelButtonClickEvent"
            >
              閉じる
            </v-btn>
            <v-btn
              color="primary"
              @click="registerButtonClickEvent"
            >
              登録
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </validation-observer>
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="final_disposal_site.prefecture_code"
      :initial-city-name="final_disposal_site.address_1"
      :initial-town-name="final_disposal_site.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </div>
</template>

<script>

import {mapActions} from 'vuex'
import PrefectureList from '../../../../mixins/PrefectureList'
import ZipCodeSearch from '../../../../components/ZipCodeSearch'
import SearchZipModal from "../../../components/SearchZipModal.vue";

export default {
  name: 'FinalDisposalSiteForm',
  components: {
    ZipCodeSearch,
    SearchZipModal
  },
  mixins: [ PrefectureList ],
  data () {
    return {
      final_disposal_site: {
        code: null,
        name: null,
        recycle_permission_number: null,
        zip_code: null,
        prefecture_code: null,
        address_1: null,
        address_2: null,
        address_3: null,
        phone_number: null,
        permission_number: null,
        disposal_method_name: null,
        processing_power: null,
        sort_priority: null
      },
      enableSearchZipModal: false,
    }
  },
  created() {
    if (this.$route.params.id) {
      this.editMasterFinalDisposalSite({ id: this.$route.params.id }).then((content) => {
        this.final_disposal_site = content
      })
    } else {
      this.newMasterFinalDisposalSite().then((content) => {
        this.final_disposal_site = content
      })
    }
  },
  methods: {
    ...mapActions('DriverMasterFinalDisposalSite', {
      newMasterFinalDisposalSite: 'new',
      editMasterFinalDisposalSite: 'edit',
      createMasterFinalDisposalSite: 'create',
      updateMasterFinalDisposalSite: 'update'
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    registerButtonClickEvent () {
      let $self = this
      this.$refs.FinalDisposalSiteFormValidator.validate().then((result) => {
        if(result){
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation(){
      if (this.$route.params.id) {
        this.updateMasterFinalDisposalSite({ id: this.$route.params.id, params: this.final_disposal_site }).then(() => {
          this.$router.push( { name: 'DriverMasterFinalDisposalSites' } )
        })
      } else {
        this.createMasterFinalDisposalSite({ params: this.final_disposal_site }).then(() => {
          this.$router.push( { name: 'DriverMasterFinalDisposalSites', params: { resetPagination: true } } )
        })
      }
    },
    cancelButtonClickEvent: function() {
      this.$router.push({ name: 'DriverMasterFinalDisposalSites' })
    },
    setZipAddress (content) {
      this.final_disposal_site.prefecture_code = `${content.pref_code}`
      this.final_disposal_site.address_1 = `${content.locality}`
      this.final_disposal_site.address_2 = `${content.street}`
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.final_disposal_site.zip_code = address.zip_code
      this.final_disposal_site.prefecture_code = `${address.prefecture_code}`
      this.final_disposal_site.address_1 = address.city
      this.final_disposal_site.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.final_disposal_site.zip_code = zipCode;
    },
  }
}
</script>
